/**
 * Redux Toolkit root store.
 *
 * Every domain slice registers its reducer in the `rootReducer` map below.
 * `RootState` is derived from `rootReducer` (not `store.getState`) to avoid
 * circular type inference when new slices are added.
 *
 * Redux DevTools are enabled only in development via RTK's built-in
 * `devTools` flag — in production builds the guard resolves to `false` and
 * DevTools code is tree-shaken out.
 */
import { combineReducers, configureStore, type Reducer, type ReducersMapObject } from '@reduxjs/toolkit';

import { accountReducer } from './accountSlice';
import { adminCategoriesReducer } from './adminCategoriesSlice';
import { adminCustomDesignRequestsReducer } from './adminCustomDesignRequestsSlice';
import { adminProductsReducer } from './adminProductsSlice';
import { artworkListenerMiddleware, artworkReducer } from './artworkSlice';
import { authReducer } from './auth';
import { cartListenerMiddleware, cartReducer } from './cart';
import { configuratorReducer } from './configurator';
import { contourReducer } from './contour';
import { notificationsReducer } from './notifications';
import { ordersReducer } from './orders';
import { pricingReducer } from './pricing';
import { productReducer } from './productSlice';
import { proofReducer } from './proof';
import { securityReducer } from './securitySlice';
import { settingsReducer } from './settingsSlice';
import { searchReducer } from './search';
import { shapeEditorReducer } from './shapeEditor';
import { shippingReducer } from './shippingSlice';
import { taxonomyReducer } from './taxonomySlice';

/**
 * Base reducer map shared by both apps. App-specific slices (e.g. the
 * Console's `adminVehicles` from CON-VEH-001) attach themselves at boot
 * via {@link extendStoreReducer} from the owning app's `main.tsx`.
 */
const baseReducers: ReducersMapObject = {
  account: accountReducer,
  adminCategories: adminCategoriesReducer,
  adminCustomDesignRequests: adminCustomDesignRequestsReducer,
  adminProducts: adminProductsReducer,
  artwork: artworkReducer,
  auth: authReducer,
  cart: cartReducer,
  configurator: configuratorReducer,
  contour: contourReducer,
  notifications: notificationsReducer,
  orders: ordersReducer,
  pricing: pricingReducer,
  product: productReducer,
  proof: proofReducer,
  search: searchReducer,
  security: securityReducer,
  shapeEditor: shapeEditorReducer,
  settings: settingsReducer,
  shipping: shippingReducer,
  taxonomy: taxonomyReducer,
};

/**
 * Per-app slice registry. Apps call {@link extendStoreReducer} from their
 * `main.tsx` BEFORE mounting `<Provider store={store}>` so the store boots
 * with every slice in place.
 *
 * Multiple calls with the same key are idempotent: the latest reducer
 * wins (handy for HMR). Re-registering with the SAME reducer reference is
 * a no-op — `replaceReducer` is only called when the map actually
 * changes.
 */
const dynamicReducers: ReducersMapObject = {};

function buildRootReducer(): Reducer {
  return combineReducers({ ...baseReducers, ...dynamicReducers });
}

export const store = configureStore({
  reducer: buildRootReducer(),
  devTools: process.env.NODE_ENV !== 'production',
  middleware: (getDefault) =>
    getDefault().prepend(cartListenerMiddleware.middleware, artworkListenerMiddleware.middleware),
});

/**
 * Attach (or replace) a reducer slice on the shared store. Intended to be
 * called by an app's `main.tsx` before any `<Provider store={store}>`
 * mounts.
 */
export function extendStoreReducer(key: string, reducer: Reducer): void {
  if (dynamicReducers[key] === reducer) return;
  dynamicReducers[key] = reducer;
  store.replaceReducer(buildRootReducer());
}

/**
 * Root state shape. Apps that register additional slices intersect their
 * own slice-state types with this base shape (see
 * `apps/console/src/stores/adminVehicleSlice.ts` for the pattern).
 */
export type RootState = {
  account: ReturnType<typeof accountReducer>;
  adminCategories: ReturnType<typeof adminCategoriesReducer>;
  adminCustomDesignRequests: ReturnType<typeof adminCustomDesignRequestsReducer>;
  adminProducts: ReturnType<typeof adminProductsReducer>;
  artwork: ReturnType<typeof artworkReducer>;
  auth: ReturnType<typeof authReducer>;
  cart: ReturnType<typeof cartReducer>;
  configurator: ReturnType<typeof configuratorReducer>;
  contour: ReturnType<typeof contourReducer>;
  notifications: ReturnType<typeof notificationsReducer>;
  orders: ReturnType<typeof ordersReducer>;
  pricing: ReturnType<typeof pricingReducer>;
  product: ReturnType<typeof productReducer>;
  proof: ReturnType<typeof proofReducer>;
  search: ReturnType<typeof searchReducer>;
  security: ReturnType<typeof securityReducer>;
  shapeEditor: ReturnType<typeof shapeEditorReducer>;
  settings: ReturnType<typeof settingsReducer>;
  shipping: ReturnType<typeof shippingReducer>;
  taxonomy: ReturnType<typeof taxonomyReducer>;
};
export type AppStore = typeof store;
export type AppDispatch = typeof store.dispatch;
